import { useState } from 'react'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm, type SubmitHandler } from 'react-hook-form'
import { useNavigate } from '@tanstack/react-router'
import axios from 'axios'
import { Loader2 } from 'lucide-react'

import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/shared/components/ui/form'
import { createOrg } from '@/entities/org/api/orgApi'
import { PlanChoiceField } from './PlanChoiceField'

const CreateOrgSchema = z.object({
  name: z.string().min(1, 'Org name is required').max(255),
  domain: z
    .string()
    .min(1, 'Domain is required')
    .max(255)
    .regex(/^[a-z0-9-]+$/, 'Lowercase letters, digits, and hyphens only'),
  // Sent with the org, not after it: the account row and the plan choice are written in one
  // transaction, so a trial always runs inside the caps of a plan somebody actually chose.
  planCode: z.string().min(1, 'Choose a plan for your trial'),
  billingInterval: z.enum(['Monthly', 'Annual']),
})

type CreateOrgValues = z.input<typeof CreateOrgSchema>

const inputClass =
  'h-12 rounded-xl border-line bg-surface-mute/50 text-[15px] transition-colors hover:bg-surface-mute focus:bg-surface focus-visible:ring-indigo-600'

type CreateOrgFeatureProps = {
  embedded?: boolean
}

export function CreateOrgFeature({ embedded = false }: CreateOrgFeatureProps) {
  const navigate = useNavigate()
  const [errorMessage, setErrorMessage] = useState<string>('')

  const form = useForm<CreateOrgValues>({
    resolver: zodResolver(CreateOrgSchema),
    defaultValues: { name: '', domain: '', planCode: '', billingInterval: 'Monthly' },
    mode: 'onTouched',
  })

  const {
    handleSubmit,
    setValue,
    watch,
    formState: { isSubmitting, errors },
  } = form

  const planCode = watch('planCode')
  const billingInterval = watch('billingInterval')

  const onSubmit: SubmitHandler<CreateOrgValues> = async (values) => {
    setErrorMessage('')
    try {
      const response = await createOrg(values)
      navigate({
        to: '/onboarding/setup/$accountId',
        params: { accountId: String(response.accountId) },
        search: { name: response.name },
      })
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 422) {
        const data = error.response.data
        const messages = Array.isArray(data)
          ? (data as Array<{ message?: string }>).map((e) => e?.message).filter(Boolean)
          : []
        setErrorMessage(messages[0] ?? 'Please check the form and try again.')
      } else if (axios.isAxiosError(error) && error.response?.status === 401) {
        setErrorMessage('Your session expired. Please sign in again.')
      } else {
        setErrorMessage('Service unavailable. Please try again later.')
      }
    }
  }

  const card = (
    <div className="rounded-2xl bg-surface p-6 ring-1 ring-line">
      <h2 className="text-xl font-bold tracking-tight text-ink">Create your organization</h2>
      <p className="mt-1.5 text-[15px] text-ink-3">
        Your team's home base for sites, guards, and daily operations.
      </p>

      {errorMessage && (
        <div className="mt-4 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          {errorMessage}
        </div>
      )}

      <Form {...form}>
        <form onSubmit={handleSubmit(onSubmit)} className="mt-5 space-y-4">
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm font-semibold text-ink-2">
                  Organization name
                </FormLabel>
                <FormControl>
                  <Input placeholder="Acme Security Co." className={inputClass} {...field} />
                </FormControl>
                <FormMessage className="text-red-500" />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="domain"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm font-semibold text-ink-2">Domain</FormLabel>
                <FormControl>
                  <Input placeholder="acme-security" className={inputClass} {...field} />
                </FormControl>
                <FormMessage className="text-red-500" />
              </FormItem>
            )}
          />
          <PlanChoiceField
            value={planCode}
            onChange={(code) => setValue('planCode', code, { shouldValidate: true })}
            interval={billingInterval}
            onIntervalChange={(interval) => setValue('billingInterval', interval)}
            error={errors.planCode?.message}
            disabled={isSubmitting}
          />
          <Button
            type="submit"
            className="h-12 w-full rounded-xl bg-indigo-600 text-[15px] font-semibold text-white shadow-md shadow-indigo-600/20 transition-all hover:bg-indigo-700 hover:shadow-lg hover:shadow-indigo-600/30 active:scale-[0.98]"
            disabled={isSubmitting}
          >
            {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Create organization
          </Button>
        </form>
      </Form>
    </div>
  )

  if (embedded) {
    return card
  }

  return (
    <div className="flex min-h-screen w-full items-center justify-center bg-surface-mute p-6">
      <div className="w-full max-w-lg">{card}</div>
    </div>
  )
}
